/**
 * Maps backend plan_id (Razorpay plan IDs from env) to RevenueCat package identifiers.
 * Configure package identifiers in the RevenueCat dashboard (Offerings → Packages).
 *
 * Env keys: VITE_RC_PKG_{TIER}_{QUARTERLY|YEARLY}, e.g. VITE_RC_PKG_ECONOMY_QUARTERLY=economy_quarterly
 */

import { isSingerOrLiveBandCategory } from '@/data/membershipPlansData';

const BACKEND_PLAN_TO_RC_PACKAGE: Record<string, string> = {};
const SINGER_LIVE_BAND_RC_PACKAGE_BY_PLAN: Record<string, string> = {};

function register(planId: string | undefined, rcPackageId: string | undefined) {
  if (planId && rcPackageId) {
    BACKEND_PLAN_TO_RC_PACKAGE[planId] = rcPackageId;
  }
}

function pickRcPackage(...candidates: Array<string | undefined>): string | undefined {
  for (const candidate of candidates) {
    if (candidate) return candidate;
  }
  return undefined;
}

function registerSingerLiveBand(
  planId: string | undefined,
  rcPackageId: string | undefined,
) {
  if (planId && rcPackageId) {
    SINGER_LIVE_BAND_RC_PACKAGE_BY_PLAN[planId] = rcPackageId;
  }
}

register(
  import.meta.env.VITE_PLAN_QUARTERLY_ECONOMY,
  pickRcPackage(
    import.meta.env.VITE_RC_PKG_ECONOMY_QUARTERLY,
    import.meta.env.VITE_RC_PKG_QUARTERLY_ECONOMY,
  ),
);
register(
  import.meta.env.VITE_PLAN_YEARLY_ECONOMY,
  pickRcPackage(
    import.meta.env.VITE_RC_PKG_ECONOMY_YEARLY,
    import.meta.env.VITE_RC_PKG_YEARLY_ECONOMY,
  ),
);
register(
  import.meta.env.VITE_PLAN_QUARTERLY_PREMIUM,
  pickRcPackage(
    import.meta.env.VITE_RC_PKG_PREMIUM_QUARTERLY,
    import.meta.env.VITE_RC_PKG_QUARTERLY_PREMIUM,
  ),
);
register(
  import.meta.env.VITE_PLAN_YEARLY_PREMIUM,
  pickRcPackage(
    import.meta.env.VITE_RC_PKG_PREMIUM_YEARLY,
    import.meta.env.VITE_RC_PKG_YEARLY_PREMIUM,
  ),
);
register(
  import.meta.env.VITE_PLAN_QUARTERLY_ELITE,
  pickRcPackage(
    import.meta.env.VITE_RC_PKG_ELITE_QUARTERLY,
    import.meta.env.VITE_RC_PKG_QUARTERLY_ELITE,
  ),
);
register(
  import.meta.env.VITE_PLAN_YEARLY_ELITE,
  pickRcPackage(
    import.meta.env.VITE_RC_PKG_ELITE_YEARLY,
    import.meta.env.VITE_RC_PKG_YEARLY_ELITE,
  ),
);

registerSingerLiveBand(
  import.meta.env.VITE_PLAN_QUARTERLY_ECONOMY,
  import.meta.env.VITE_RC_PKG_SLB_ECONOMY_QUARTERLY,
);
registerSingerLiveBand(
  import.meta.env.VITE_PLAN_YEARLY_ECONOMY,
  import.meta.env.VITE_RC_PKG_SLB_ECONOMY_YEARLY,
);
registerSingerLiveBand(
  import.meta.env.VITE_PLAN_QUARTERLY_PREMIUM,
  import.meta.env.VITE_RC_PKG_SLB_PREMIUM_QUARTERLY,
);
registerSingerLiveBand(
  import.meta.env.VITE_PLAN_YEARLY_PREMIUM,
  import.meta.env.VITE_RC_PKG_SLB_PREMIUM_YEARLY,
);
registerSingerLiveBand(
  import.meta.env.VITE_PLAN_QUARTERLY_ELITE,
  import.meta.env.VITE_RC_PKG_SLB_ELITE_QUARTERLY,
);
registerSingerLiveBand(
  import.meta.env.VITE_PLAN_YEARLY_ELITE,
  import.meta.env.VITE_RC_PKG_SLB_ELITE_YEARLY,
);

/** RevenueCat offering identifier (dashboard default is often "default"). */
export const RC_DEFAULT_OFFERING_ID =
  import.meta.env.VITE_RC_OFFERING_ID || 'default';

export function getRcPackageIdForBackendPlan(
  planId: string,
  categoryName?: string | null,
): string | null {
  if (isSingerOrLiveBandCategory(categoryName)) {
    const slbPackage = SINGER_LIVE_BAND_RC_PACKAGE_BY_PLAN[planId];
    if (slbPackage) return slbPackage;
  }
  return BACKEND_PLAN_TO_RC_PACKAGE[planId] ?? null;
}

export function isIapProductMappingConfigured(): boolean {
  return Object.keys(BACKEND_PLAN_TO_RC_PACKAGE).length > 0;
}
